<script setup lang="ts">
const { isLegalAgePopupEnabled } = useFeatureFlagsNew();
const { isModalLegalAgeOpen } = storeToRefs(useLayoutStore());
const { closeLegalAge } = useLayoutStore();

// Detecção de bots para bypass automático
const { isBot } = usePerformanceBotBypass();

const isButtonNoTouched = ref(false);

function onButtonNoClick() {
  isButtonNoTouched.value = true;
}

function onButtonCloseClick() {
  isButtonNoTouched.value = false;
}

const cookieName = 'legal_age_popup_closed';

const wasClosedPreviously = useCookie(cookieName, {
  default: () => false,
  path: '/',
  expires: new Date(new Date().setDate(new Date().getDate() + 30)),
  // expires: new Date(),
});

function onButtonYesClick() {
  isButtonNoTouched.value = false;

  closeLegalAge();

  wasClosedPreviously.value = true;
}

const isLegalAgeModalVisible = computed(() => {
  // Nunca mostrar modal para bots (Googlebot, Lighthouse, GTmetrix)
  if (isBot.value) {
    return false;
  }

  if (!isLegalAgePopupEnabled.value) {
    return false;
  }

  return isModalLegalAgeOpen.value && !wasClosedPreviously.value;
});
</script>

<template>
  <Teleport
    v-if="isLegalAgeModalVisible"
    to="body"
  >
    <div :class="$style.verificationModal">
      <div :class="$style.modalInner">
        <div :class="$style.logoContainer">
          <BaseCommonLogo
            always-logo
            :class="$style.logo"
            custom-size
          />

          <button
            v-if="isButtonNoTouched"
            :class="$style.closeButton"
            @click="onButtonCloseClick"
          >
            <NuxtIcon name="close" />
          </button>
        </div>

        <div :class="$style.modalContent">
          <div :class="$style.title">
            <span v-if="!isButtonNoTouched">
              {{ $t('components.legal_age.title') }}
            </span>
            <span v-else>
              {{ $t('components.legal_age.no_description') }}
            </span>
          </div>

          <div
            v-if="!isButtonNoTouched"
            :class="$style.buttons"
          >
            <button
              :class="[$style.button, $style.buttonNo]"
              @click="onButtonNoClick"
            >
              <NuxtIcon name="close" />
              {{ $t('general.no') }}
            </button>
            <button
              :class="[$style.button, $style.buttonYes]"
              @click="onButtonYesClick"
            >
              <NuxtIcon name="check" />
              {{ $t('general.yes') }}
            </button>
          </div>

          <p
            v-if="!isButtonNoTouched"
            :class="$style.disclaimer"
          >
            {{ $t('components.legal_age.disclaimer') }}
          </p>
        </div>
      </div>
      <div :class="$style.modalBackdrop" />
    </div>
  </Teleport>
</template>

<style module lang="postcss">
.verificationModal {
  /* Otimizado: backdrop mais leve para melhor performance */
  @apply fixed top-0 left-0 w-full h-full bg-black/50 z-[999] flex items-center justify-center;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);

  .modalInner {
    @apply relative z-[20] w-full max-w-[82vw] md:max-w-[400px] h-auto mx-auto transform rounded-lg overflow-hidden text-left align-middle shadow-xl bg-bg-primary select-none;

    .logoContainer {
      @apply w-full flex items-center justify-center bg-auth-texts/5 p-6;

      .logo {
        @apply w-full max-w-40 h-6 flex items-center justify-center m-0 p-0;
      }

      .closeButton {
        @apply absolute top-0 right-0 text-auth-texts/60 text-sm p-4 cursor-pointer;
      }
    }

    .modalContent {
      @apply w-full flex flex-col items-center justify-center gap-6 p-4 xs:p-8;

      .title {
        @apply text-lg sm:text-2xl font-bold text-center text-white max-w-40 sm:max-w-52 -mt-1;
      }

      .buttons {
        @apply w-full flex items-center justify-center gap-4;

        button {
          @apply w-full h-10 rounded-lg border-solid border flex items-center justify-center gap-1;

          svg {
            @apply text-sm;
          }

          &.buttonNo {
            @apply bg-auth-texts/5 text-auth-texts/70 font-medium border-auth-texts/50;
          }

          &.buttonYes {
            @apply bg-button-bg text-button-text font-bold border-primary;
          }
        }
      }

      .disclaimer {
        @apply text-xs text-center text-auth-texts/50 leading-snug -mt-2;
      }
    }
  }

  .modalBackdrop {
    @apply fixed top-0 left-0 w-full h-full bg-black/5 z-[10] pointer-events-auto touch-auto;
  }
}
</style>
